/**
 * Helpers for `TimelineEvent[]` — the `history` array on a manufacturer.
 *
 * Pure, with no Firebase import, so the rules here can be tested without
 * booting the app (see the note in `services/calibreSources.ts` for what
 * importing `services/firebase` into a test costs).
 */

import type { TimelineEvent, TimelineEventImage } from '@watchkeeper/shared-types';

/**
 * The year a timeline event sorts on, or `Infinity` when the date says no year.
 *
 * `TimelineEvent.date` is a free-form string and never a `Date`, because the
 * imprecision is itself the fact — `"1930s"`, `"circa 1885"`, `"Between 1943
 * and 1948"` all say something a parsed date would throw away. So sorting pulls
 * out the first four-digit run and leaves the string alone.
 *
 * FIRST run, not the smallest or the last: `"Between 1943 and 1948"` is an event
 * that began in 1943, and a range sorts from its start. Un-parseable dates
 * return `Infinity` so they collect at the END rather than silently claiming
 * year zero and heading the timeline.
 *
 * Deliberately NOT `\b\d{4}\b`: there is no word boundary between the `0` and
 * the `s` of `"1930s"`, so the anchored form fails on the single most common
 * imprecise date in horology.
 */
export function parseTimelineYear(date: string | null | undefined): number {
  const match = /\d{4}/.exec(date ?? '');
  return match ? Number(match[0]) : Infinity;
}

/**
 * Chronological order, un-dated events last.
 *
 * Returns a NEW array — callers render from this while editing the source
 * array, and an in-place sort would reorder rows under the cursor.
 *
 * `Array.prototype.sort` is stable, so events sharing a year (or sharing
 * `Infinity`) keep the order the curator put them in. That is the only ordering
 * signal left once the year ties, and re-deriving one would move rows for no
 * reason a curator could predict.
 */
export function sortTimelineEvents(events: readonly TimelineEvent[]): TimelineEvent[] {
  return [...events].sort(
    (a, b) => parseTimelineYear(a.date) - parseTimelineYear(b.date),
  );
}

function cleanImage(image: TimelineEventImage | undefined): TimelineEventImage | undefined {
  if (!image) return undefined;
  const storagePath = image.storagePath?.trim() || undefined;
  const url = image.url?.trim() || undefined;
  const caption = image.caption?.trim() || undefined;
  const aiSuggestion = image.aiSuggestion?.trim() || undefined;
  // An image row carrying nothing but its source attribution describes no
  // image at all — an `aiSuggestion` alone DOES (it is the "curator, go and
  // find this" signal), which is why it counts here.
  if (!storagePath && !url && !caption && !aiSuggestion) return undefined;
  return {
    // Required by the type and used for attribution; curator uploads are ours.
    source: image.source?.trim() || 'watchkeeper',
    ...(storagePath ? { storagePath } : {}),
    ...(url ? { url } : {}),
    ...(caption ? { caption } : {}),
    ...(aiSuggestion ? { aiSuggestion } : {}),
  };
}

/**
 * One event, safe to hand to Firestore.
 *
 * `updateMovementManufacturer` strips undefined only at the TOP level, so an
 * `undefined` sitting inside an array element reaches the SDK and throws —
 * the same shallow-strip failure recorded against reference gallery images.
 * Nearly every field on a `TimelineEvent` is optional, and its `image` nests a
 * second layer of them, so writing the objects as the form holds them would
 * throw on any event a curator left partly filled.
 *
 * Empty strings are dropped rather than stored: an untouched Source URL field
 * hands back `''`, and persisting that makes "no source" indistinguishable from
 * "a source that is the empty string" for every later reader.
 */
export function timelineEventForWrite(event: TimelineEvent): TimelineEvent {
  const category = event.category;
  const sourceUrl = event.sourceUrl?.trim() || undefined;
  const image = cleanImage(event.image);
  const aiConfidence =
    typeof event.aiConfidence === 'number' && Number.isFinite(event.aiConfidence)
      ? event.aiConfidence
      : undefined;

  return {
    date: event.date?.trim() ?? '',
    title: event.title?.trim() ?? '',
    // Rich-text HTML — trimmed only at the ends, never otherwise touched.
    description: event.description?.trim() ?? '',
    ...(category ? { category } : {}),
    ...(sourceUrl ? { sourceUrl } : {}),
    ...(image ? { image } : {}),
    ...(aiConfidence !== undefined ? { aiConfidence } : {}),
  };
}

/** The whole array, sorted and cleaned — what a save actually writes. */
export function timelineForWrite(events: readonly TimelineEvent[]): TimelineEvent[] {
  return sortTimelineEvents(events).map(timelineEventForWrite);
}

/** Category options for the per-event dropdown, in display order. */
export const TIMELINE_CATEGORIES = [
  'founding',
  'product',
  'milestone',
  'acquisition',
  'absorption',
  'closure',
  'other',
] as const;

export type TimelineCategory = (typeof TIMELINE_CATEGORIES)[number];

/** Display label for a category value, falling back to the raw value. */
export function timelineCategoryLabel(value: string | undefined): string {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** A blank event, for the Add button. */
export function emptyTimelineEvent(): TimelineEvent {
  return { date: '', title: '', description: '' };
}
